import React, { Fragment, useState, useEffect } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { fetchData, postData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import Select from 'react-select';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Tooltip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import './CustomDatePicker.css';

const StaffCreate = (props) => {
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    useEffect(() => {
        // Check if the token is expired
        if (isAuthenticated && tokenIsExpired(token)) {
            // Token is expired, log out the user
            logout();
            return;
        }
    }, [isAuthenticated, companyId, token, logout]);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedUserType, setSelectedUserType] = useState(null);
    const [userTypeOptions, setUserTypeOptions] = useState([]);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phonenumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [isLoginNeeded, setIsLoginNeeded] = useState(false);
    const [dateOfJoining, setDateOfJoining] = useState("");
    const [dateOfLeaving, setDateOfLeaving] = useState("");
    const [dOB, setDOB] = useState("");
    const [bloodGroup, setBloodGroup] = useState("");
    const [idcardtype, setIdCardType] = useState("");
    const [idcardno, setIdCardNo] = useState("");
    const [staffnote, setStaffNote] = useState("");
    const [licenseNo, setLicenseNo] = useState("");
    const [expirydate, setExpiryDate] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const clearForm = () => {
        setFirstName("");
        setLastName("");
        setPhoneNumber("");
        setEmail("");
        setStaffNote("");
        setDateOfJoining("");
        setDateOfLeaving("");
        setDOB("");
        setCode("");
        setIdCardType("");
        setIdCardNo("");
        setBloodGroup("");
        setLicenseNo("");
        setExpiryDate("");
    };

    const fetchUserTypes = async () => {
        try {
            const response = await fetchData('UserTypeApi', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                const options = response.response_data.map(userType => ({
                    value: userType.id,
                    label: userType.name
                }));
                setUserTypeOptions(options);
            }
        } catch (error) {
            console.error('Error fetching user types:', error);
        }
    };

    const handleUserTypeChange = (selectedOption) => {
        setSelectedUserType(selectedOption);
    };

    useEffect(() => {
        fetchUserTypes();
    }, []);

    const submitData = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const createData = {
            UserTypeId: selectedUserType ? selectedUserType.value : "", // Handle case when selectedUserType is null
            FirstName: firstName,
            LastName: lastName,
            Code: code,
            Email: email,
            PhoneNumber: phonenumber,
            DateOfJoining: dateOfJoining,
            DateOfLeaving: dateOfLeaving,
            DateOfBirth: dOB,
            BloodGroup: bloodGroup,
            Idcard: idcardtype,
            IdCardNumber: idcardno,
            Staffnote: staffnote,
            LicenseNo: selectedUserType && selectedUserType.label === 'Driver' ? licenseNo : null,
            Expirydate: selectedUserType && selectedUserType.label === 'Driver' ? expirydate : null,
            CreatedBy: userId,
            CompanyID: companyId,
            CreatedDate: new Date(),
            IsLoginNeeded: isLoginNeeded,
        };

        try {
            const response = await postData('StaffApi', createData, token);
            console.log('Staff Created success:', response);
            setSuccessMessage("Staff Created successfully!");
            setErrorMessage("");
            clearForm();
            history.push('/StaffIndex');
            setTimeout(() => {
                setSuccessMessage("");
            }, 3000);
        } catch (error) {
            console.error('Staff Create error:', error);
            if (error.response) {
                const errors = error.response.data;
                if (errors && errors.length > 0) {
                    setErrorMessage(errors);
                } else {
                    setErrorMessage("Oops! Something went wrong.");
                }
            } else {
                setErrorMessage("Oops! Something went wrong.");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Fragment>
            <div className="w-full max-w-sm container mt-6 mx-auto">
                <form onSubmit={submitData}>
                    <div className='w-full mb-2 flex items-center'>
                        <h4 className='psheading flex-grow linkcolor' style={{ fontFamily: 'Times New Roman', color: 'black' }}>Create</h4>
                        <Tooltip title="Exit">
                            <Link to="/StaffIndex" style={{ color: 'red', textDecoration: 'none', fontWeight: 'bolder' }}>
                                <Button style={{ color: 'red', fontWeight: 'bolder' }}>
                                    Exit <ExitToAppIcon />
                                </Button>
                            </Link>
                        </Tooltip>
                        <Tooltip title="SAVE">
                            <Button type="submit" style={{ color: 'darkolivegreen', fontWeight: 'bolder', marginLeft: '2px' }} disabled={isLoading}>
                                {isLoading ? <CircularProgress size={24} color="inherit" /> : "SAVE"} <CheckCircleIcon />
                            </Button>
                        </Tooltip>
                    </div>
                    {errorMessage && <p className="text-red-500">{errorMessage}</p>}
                    {successMessage && <p className="text-green-500">{successMessage}</p>}
                    <div className="w-full mb-4 flex">
                        <div className="w-1/5 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="user type">
                                User Type
                                <span className="text-red-500">*</span>
                            </label>
                            <Select
                                value={selectedUserType}
                                onChange={handleUserTypeChange}
                                options={userTypeOptions}
                                placeholder="Select User Type"
                                isSearchable
                            />
                        </div>
                        <div className="w-1/5 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="code">
                                First Name
                                <span className="text-red-500">*</span>
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                type="text" placeholder="Enter First Name"
                                maxLength={30}
                            />
                        </div>
                        <div className="w-1/5 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="code">
                                Last Name
                                <span className="text-red-500">*</span>
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                type="text" placeholder="Enter Last Name"
                                maxLength={30}
                            />
                        </div>
                        <div className="w-1/5 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="code">
                                Code
                                <span className="text-red-500">*</span>
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                type="text" placeholder="Enter Code"
                                maxLength={30}
                            />
                        </div>
                        <div className="w-1/5 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="email">
                                Email
                                <span className="text-red-500">*</span>
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                type="email" placeholder="Enter Email"
                                maxLength={50}
                            />
                        </div>
                    </div>
                    <div className="w-full mb-4 flex">
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="phonenumber">
                                Phone Number
                                <span className="text-red-500">*</span>
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={phonenumber}
                                onChange={(e) => setPhoneNumber(e.target.value)}
                                type="text" placeholder="Enter Phone Number"
                                maxLength={15}
                            />
                        </div>
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="dOB">
                                Date of Birth
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={dOB}
                                onChange={(e) => setDOB(e.target.value)}
                                type="date" placeholder="Enter Date of Birth"
                            />
                        </div>
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="dateOfJoining">
                                Date of Joining
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={dateOfJoining}
                                onChange={(e) => setDateOfJoining(e.target.value)}
                                type="date" placeholder="Enter Date of Joining"
                            />
                        </div>
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="dateOfLeaving">
                                Date of Leaving
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={dateOfLeaving}
                                onChange={(e) => setDateOfLeaving(e.target.value)}
                                type="date" placeholder="Enter Date of Leaving"
                            />
                        </div>
                    </div>
                    <div className="w-full mb-4 flex">
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="bloodGroup">
                                Blood Group
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={bloodGroup}
                                onChange={(e) => setBloodGroup(e.target.value)}
                                type="text" placeholder="Enter Blood Group"
                                maxLength={5}
                            />
                        </div>
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="idcardtype">
                                ID Card Type
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={idcardtype}
                                onChange={(e) => setIdCardType(e.target.value)}
                                type="text" placeholder="Enter ID Card Type"
                                maxLength={30}
                            />
                        </div>
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="idcardno">
                                ID Card Number
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={idcardno}
                                onChange={(e) => setIdCardNo(e.target.value)}
                                type="text" placeholder="Enter ID Card Number"
                                maxLength={30}
                            />
                        </div>
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="licenseNo">
                                License No
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={licenseNo}
                                onChange={(e) => setLicenseNo(e.target.value)}
                                type="text" placeholder="Enter License Number"
                                maxLength={30}
                                disabled={selectedUserType && selectedUserType.label !== 'Driver'}
                            />
                        </div>
                    </div>
                    <div className="w-full mb-4 flex">
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="expirydate">
                                Expiry Date
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={expirydate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                                type="date" placeholder="Enter Expiry Date"
                                disabled={selectedUserType && selectedUserType.label !== 'Driver'}
                            />
                        </div>
                        <div className="w-1/4 mr-2">
                            <label className="block tracking-wide text-black text-base font-bold mb-2" htmlFor="staffnote">
                                Staff Note
                            </label>
                            <input className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={staffnote}
                                onChange={(e) => setStaffNote(e.target.value)}
                                type="text" placeholder="Enter Staff Note"
                                maxLength={255}
                            />
                        </div>
                        <div className="w-1/4 mr-2 ">
                            <input
                                className="mr-2 leading-tight"
                                type="checkbox"
                                checked={isLoginNeeded}
                                onChange={(e) => setIsLoginNeeded(e.target.checked)}
                            />
                            <label className="text-base text-black font-bold" htmlFor="isLoginNeeded">
                                Is Login Needed?
                            </label>
                        </div>
                    </div>
                </form>
            </div>
        </Fragment>
    );
};

export default StaffCreate;
